const SHORTLIST_STORAGE_KEY = "member-shortlists-v1"
const INTEREST_STORAGE_KEY = "member-interests-v1"
const NOTIFICATION_STORAGE_KEY = "member-interest-notifications-v1"

const safeReadJson = (key, fallback) => {
    if (typeof window === "undefined") return fallback

    try {
        const rawValue = window.localStorage.getItem(key)
        return rawValue ? JSON.parse(rawValue) : fallback
    } catch (error) {
        return fallback
    }
}

const safeWriteJson = (key, value) => {
    if (typeof window === "undefined") return

    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
        // ignore storage write failures in preview mode
    }
}

const normalizeText = (value) => (value || "").toString().trim().toLowerCase()

const getFullName = (user) =>
    [user?.firstName, user?.lastName, user?.surname]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim() || "Member"

const calculateAge = (birthdate) => {
    if (!birthdate) return null

    const today = new Date()
    const birthDate = new Date(birthdate)
    if (Number.isNaN(birthDate.getTime())) return null

    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1
    }

    return age
}

const getCurrentMemberShortlists = () => safeReadJson(SHORTLIST_STORAGE_KEY, {})
const getCurrentMemberInterests = () => safeReadJson(INTEREST_STORAGE_KEY, {})
const getCurrentNotifications = () => safeReadJson(NOTIFICATION_STORAGE_KEY, {})

export const getShortlistedIds = (memberId) => {
    if (!memberId) return []

    const shortlists = getCurrentMemberShortlists()
    return shortlists[memberId] || []
}

export const toggleShortlistedProfile = (memberId, profileId) => {
    if (!memberId || !profileId) return []

    const shortlists = getCurrentMemberShortlists()
    const currentIds = shortlists[memberId] || []
    const nextIds = currentIds.includes(profileId)
        ? currentIds.filter((id) => id !== profileId)
        : [...currentIds, profileId]

    safeWriteJson(SHORTLIST_STORAGE_KEY, {
        ...shortlists,
        [memberId]: nextIds,
    })

    return nextIds
}

export const getSentInterestIds = (memberId) => {
    if (!memberId) return []

    const interests = getCurrentMemberInterests()
    return Object.keys(interests[memberId] || {})
}

export const hasInterestBeenSent = (memberId, profileId) => {
    if (!memberId || !profileId) return false

    const interests = getCurrentMemberInterests()
    return Boolean(interests?.[memberId]?.[profileId])
}

export const getInterestNotifications = (memberId) => {
    if (!memberId) return []

    const notifications = getCurrentNotifications()
    return notifications[memberId] || []
}

export const sendInterestNotification = ({ sender, receiver }) => {
    if (!sender?._id || !receiver?._id) {
        return {
            sentInterestIds: [],
            recipientNotifications: [],
        }
    }

    const now = new Date().toISOString()
    const interests = getCurrentMemberInterests()
    const senderInterests = interests[sender._id] || {}

    if (!senderInterests[receiver._id]) {
        interests[sender._id] = {
            ...senderInterests,
            [receiver._id]: {
                sentAt: now,
                senderId: sender._id,
                receiverId: receiver._id,
            },
        }

        safeWriteJson(INTEREST_STORAGE_KEY, interests)

        const notifications = getCurrentNotifications()
        const receiverNotifications = notifications[receiver._id] || []
        const nextNotification = {
            id: `${sender._id}-${receiver._id}-${now}`,
            senderId: sender._id,
            senderName: getFullName(sender),
            senderRefId: sender?.refId || "-",
            receiverId: receiver._id,
            createdAt: now,
            message: `${getFullName(sender)} showed interest in your profile.`,
        }

        notifications[receiver._id] = [nextNotification, ...receiverNotifications]
            .filter((item, index, items) => items.findIndex((match) => match.senderId === item.senderId) === index)
            .slice(0, 10)

        safeWriteJson(NOTIFICATION_STORAGE_KEY, notifications)
    }

    return {
        sentInterestIds: Object.keys(interests[sender._id] || {}),
        recipientNotifications: getInterestNotifications(receiver._id),
    }
}

const buildLocationParts = (profile) => {
    const presentAddress = profile?.contactInformation?.presentAddress || {}
    const nativeAddress = profile?.contactInformation?.nativeAddress || {}

    return [
        profile?.occupationLocation,
        presentAddress?.city,
        presentAddress?.state,
        nativeAddress?.city,
        nativeAddress?.state,
        profile?.familyInformation?.fatherNative,
        profile?.familyInformation?.motherNative,
    ]
        .map(normalizeText)
        .filter(Boolean)
}

const pushReason = (reasons, value) => {
    if (!value || reasons.includes(value) || reasons.length >= 3) return
    reasons.push(value)
}

const scoreSingleProfile = (currentMember, profile) => {
    const reasons = []
    let score = 0

    const currentAge = calculateAge(currentMember?.dateOfBirth)
    const matchAge = calculateAge(profile?.dateOfBirth)
    const currentLocations = buildLocationParts(currentMember)
    const matchLocations = buildLocationParts(profile)
    const sharedLocations = currentLocations.filter((place) => matchLocations.includes(place))

    if (sharedLocations.length > 0) {
        score += 22
        pushReason(reasons, "Same area")
    }

    if (
        normalizeText(currentMember?.maritalStatus) &&
        normalizeText(currentMember?.maritalStatus) === normalizeText(profile?.maritalStatus)
    ) {
        score += 18
        pushReason(reasons, "Same marital status")
    }

    if (
        normalizeText(currentMember?.additionalInformation?.star) &&
        normalizeText(currentMember?.additionalInformation?.star) === normalizeText(profile?.additionalInformation?.star)
    ) {
        score += 12
        pushReason(reasons, "Same star")
    }

    if (
        normalizeText(currentMember?.additionalInformation?.zodiacSign) &&
        normalizeText(currentMember?.additionalInformation?.zodiacSign) === normalizeText(profile?.additionalInformation?.zodiacSign)
    ) {
        score += 10
        pushReason(reasons, "Same zodiac")
    }

    if (typeof currentAge === "number" && typeof matchAge === "number") {
        const ageDiff = Math.abs(currentAge - matchAge)

        if (ageDiff <= 2) {
            score += 18
            pushReason(reasons, "Close age")
        } else if (ageDiff <= 5) {
            score += 10
            pushReason(reasons, "Suitable age")
        }
    }

    const expectedOccupations = Array.isArray(currentMember?.additionalInformation?.expectedOccupation)
        ? currentMember.additionalInformation.expectedOccupation.map((item) => normalizeText(item?.value || item?.text || item))
        : [normalizeText(currentMember?.additionalInformation?.expectedOccupation)].filter(Boolean)

    const candidateOccupation = normalizeText(profile?.occupationDetail || profile?.occupation)
    if (candidateOccupation && expectedOccupations.some((occupation) => candidateOccupation.includes(occupation))) {
        score += 15
        pushReason(reasons, "Matches preferred work")
    }

    if (reasons.length === 0) {
        pushReason(reasons, "Suggested for you")
    }

    return {
        ...profile,
        __matchScore: score,
        __matchReasons: reasons,
    }
}

export const rankMatchProfiles = (currentMember, profiles = []) => {
    if (!currentMember?._id) return []

    const preferredProfileType = currentMember?.profileType === "bride"
        ? "groom"
        : currentMember?.profileType === "groom"
            ? "bride"
            : ""

    return profiles
        .filter((profile) => profile?._id && profile._id !== currentMember._id)
        .filter((profile) => profile?.activeStat === "A")
        .filter((profile) => profile?.status === 1)
        .filter((profile) => !preferredProfileType || profile?.profileType === preferredProfileType)
        .map((profile) => scoreSingleProfile(currentMember, profile))
        .sort((left, right) => {
            if (right.__matchScore !== left.__matchScore) return right.__matchScore - left.__matchScore

            const leftTime = new Date(left?.createdAt || 0).getTime()
            const rightTime = new Date(right?.createdAt || 0).getTime()
            return rightTime - leftTime
        })
}

